<template>
    <card-skeleton data-id="issuesCard" :is-loading="isLoading">
        <v-card-item>
            <card-title :text="t('inquiry.inquiriesTitle')">
                <card-button class="pa-2" size="large" :to="{ name: routeNames.issueSubmit }" data-id="newInquiryButton">
                    {{ t('inquiry.newInquiry') }}
                </card-button>
            </card-title>
        </v-card-item>
        <v-card-text class="text-body-1 pt-0">
            <issues-card-list :issues="issuesList" :loading="isLoading" :is-issues-list-empty="isIssuesListEmpty" :is-page-list="false" />
            <div v-if="!isIssuesListEmpty" class="text-center ma-0">
                <load-more-button data-id="issuesLoadMoreButton" :disabled="isLoadMoreDisabled" @click="handleLoadMore" :text="issuesButtonText" />
            </div>
        </v-card-text>
    </card-skeleton>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CardButton from '@/features/common/components/CardButton.vue';
import { routeNames } from '@/router';
import LoadMoreButton from '@/features/common/components/LoadMoreButton.vue';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import CardTitle from '@/features/common/components/CardTitle.vue';
import IssuesCardList from '../components/list/IssuesCardList.vue';
import { getIssues } from '../services/api';
import { useIssueStore } from '../store/cardStore';
import { getFirst } from '@/helpers';
import { IIssuesCardBlock, IssueDto, IssuesDto } from '../types';
import { TAKE_OPTIONS } from '@/constants';
import { storeToRefs } from 'pinia';
const props = withDefaults(defineProps<IIssuesCardBlock>(), { take: TAKE_OPTIONS.DEFAULT });

const issues = ref<IssuesDto>();
const isLoading = ref(true);
const take = ref<number>(props.take);

const expanded = ref(false);

const issueStore = useIssueStore();
const { items } = storeToRefs(issueStore);
const { setItems, isMaxReached } = issueStore;

const { t } = useI18n();

const getAllIssues = async () => {
    try {
        isLoading.value = true;
        const response = await getIssues();
        setItems(response?.data);
    } catch {
        issues.value = undefined;
    } finally {
        isLoading.value = false;
    }
};

const handleLoadMore = (): void => {
    if (expanded.value && isMaxReached(take.value)) {
        take.value = props.take;
        expanded.value = false;
        return;
    }

    take.value += props.take;
    if (take.value > props.take) expanded.value = true;
};

const issuesList = computed(() => getFirst(items.value as IssueDto[], take.value));

const isIssuesListEmpty = computed(() => {
    return !issuesList.value?.length;
});


const isLoadMoreDisabled = computed(() => {
    return isLoading.value || !items.value?.length || (isMaxReached(take.value) && !expanded.value);
});

const issuesButtonText = computed(() => {
    return expanded.value && isMaxReached(take.value) ? 'startPage.showLess' : 'startPage.loadMore';
});

onMounted(() => {
    getAllIssues();
});
</script>
